
import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export function DesktopTransactionTable({
  transactions,
  getStatusColor,
  getTypeColor,
  getTypeLabel,
  formatCurrency,
  handleEdit,
  handleDelete
}) {
  const [expandedTransactions, setExpandedTransactions] = useState({});

  const toggleExpand = (id) => {
    setExpandedTransactions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch (e) {
      return dateString;
    }
  };
  
  const formatUserInfo = (userId, createdAt) => {
    if (!userId) return <span className="text-gray-500 text-xs">—</span>;
    
    // Always show email if available
    if (userId.includes('@')) {
      return (
        <div className="flex flex-col">
          <span className="text-xs font-medium">{userId}</span>
          {createdAt && <span className="text-xs text-gray-500">{formatDateTime(createdAt)}</span>}
        </div>
      );
    }
    
    // For non-email users, just show "Usuário" instead of UUID
    return (
      <div className="flex flex-col">
        <span className="text-xs font-medium">Usuário</span>
        {createdAt && <span className="text-xs text-gray-500">{formatDateTime(createdAt)}</span>}
      </div>
    );
  };

  return (
    <div className="rounded-md overflow-hidden">
      <Table className="border-collapse min-w-full">
        <TableHeader className="bg-slate-800">
          <TableRow>
            <TableHead className="w-[100px] text-white">Tipo</TableHead>
            <TableHead className="text-white">Descrição</TableHead>
            <TableHead className="text-white">Valor</TableHead>
            <TableHead className="text-white">Data</TableHead>
            <TableHead className="text-white">Status</TableHead>
            <TableHead className="text-white">Registrado por</TableHead>
            <TableHead className="w-[100px] text-right text-white">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                Nenhuma transação encontrada
              </TableCell>
            </TableRow>
          ) : (
            transactions.map((transaction) => (
              <TableRow key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <TableCell className={`font-medium ${getTypeColor(transaction.type)}`}>
                  {getTypeLabel(transaction.type)}
                </TableCell>
                <TableCell className="max-w-[300px]">
                  <div onClick={() => toggleExpand(transaction.id)} className="cursor-pointer">
                    {transaction.type === "entrada" ? (
                      <>
                        <div className="font-medium">
                          {transaction.customers?.name || "Cliente não especificado"}
                        </div>
                        {transaction.description && (
                          <div className={expandedTransactions[transaction.id] ? "whitespace-normal" : "truncate text-sm text-gray-500"}>
                            {transaction.description}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="font-medium">
                          {transaction.name || transaction.category || "—"}
                        </div>
                        {transaction.description && (
                          <div className={expandedTransactions[transaction.id] ? "whitespace-normal" : "truncate text-sm text-gray-500"}>
                            {transaction.description}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell className={`font-medium ${getTypeColor(transaction.type)}`}>
                  {formatCurrency(transaction.amount)}
                </TableCell>
                <TableCell>
                  {transaction.due_date
                    ? format(new Date(transaction.due_date), "dd/MM/yyyy", { locale: ptBR })
                    : "—"}
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(transaction.payment_status)}`}>
                    {transaction.payment_status}
                  </span>
                </TableCell>
                <TableCell className="text-sm">
                  {formatUserInfo(transaction.created_by, transaction.created_at)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(transaction)}
                      className="h-8 w-8"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(transaction)}
                      className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
